import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { href } from "../lib/route";
import { nameProblem } from "../lib/programBoard";

/**
 * `#/programs/new` — NAME THE BLOCK, THEN PLAN IT (`docs/PROGRAMS-DESIGN.md` §4).
 *
 * ⚠️ A NAME AND NOTHING ELSE, AND THAT IS A DECISION. The board is where a program is built; a
 * create form that also asked for weeks, a description and a start date would be a second
 * editor, and the second one always drifts. `name` is asked because the table's CHECK requires
 * a non-empty one (§2.1) — everything else the board can change in place.
 *
 * ⚠️ AND IT IS A PAGE RATHER THAN A WINDOW, for the same reason the board is: it leads straight
 * into the board, and a window that closes into a different screen is a navigation wearing a
 * dismissal's clothes.
 */
export function ProgramNew({ coachId, foreground = true, onSaved }: {
  coachId: string;
  foreground?: boolean;
  /** The list refetches — it is reading the table this just wrote to. */
  onSaved: () => void;
}) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const field = useRef<HTMLInputElement>(null);

  useEffect(() => { if (foreground) heading.current?.focus(); }, [foreground]);

  async function create() {
    const trimmed = name.trim();
    /* The same 1–120 btrim rule the table's CHECK carries (§2.1), stated here so the refusal is
       legible before the round trip rather than arriving as a constraint name — `nameProblem`, Rename's too. */
    const said = nameProblem(name);
    if (said !== null) { setProblem(said); field.current?.focus(); return; }
    setSaving(true); setProblem(null);
    const { data, error } = await supabase
      .from("coach_programs")
      .insert({ coach_id: coachId, name: trimmed })
      .select("id")
      .limit(1);
    setSaving(false);
    if (error !== null) {
      /* The server's own words: here it is the CHECK or the owner policy, both worth seeing. */
      setProblem(error.message);
      return;
    }
    const created = (data ?? [])[0];
    if (created === undefined) {
      /* ⚠️ A SUCCEEDED INSERT THAT RETURNS NOTHING IS NOT A SUCCESS THIS SCREEN CAN ACT ON —
         there is no id to navigate to. Saying so beats sending the coach to a blank board. */
      setProblem("The program was created but its address could not be read. Open it from the list.");
      onSaved();
      return;
    }
    onSaved();
    window.location.hash = href({ name: "program", programId: created.id });
  }

  return (
    <main id="main" className="workouts-page program-new">
      <header className="page-head">
        <p className="page-eyebrow">
          <a className="ht-link" href={href({ name: "programs" })}>← All programs</a>
        </p>
        <h1 className="ht-title" tabIndex={-1} ref={heading}>New program</h1>
      </header>

      <p className="ht-body program-new-says">
        A program is a block of weeks you plan once and give to any client from any Monday. Name
        it now; you add the weeks on the next screen.
      </p>

      <div className="ht-field program-new-field">
        <label className="ht-field-label" htmlFor="program-name">Name</label>
        <input id="program-name" ref={field} className="ht-input" value={name}
               maxLength={120}
               onChange={(e) => { setName(e.target.value); setProblem(null); }}
               onKeyDown={(e) => { if (e.key === "Enter") void create(); }} />
      </div>

      {problem !== null && <p className="ht-callout program-error" role="alert">{problem}</p>}

      <div className="program-new-actions">
        <button type="button" className="ht-button ht-button-primary" disabled={saving}
                onClick={() => { void create(); }}>
          {saving ? "Creating…" : "Create program"}
        </button>
      </div>
    </main>
  );
}
